// RESPONSABILIDAD:
// - Muestra información de la sala
// - Lista miembros
// - Permite abandonar la sala
// - Gestión de permisos de miembros (solo admin/owner lo ve)
// - Admin/Owner: mutear / desmutear / bloquear / desbloquear
// - Owner: promover_admin / quitar_admin / expulsar
//
// Importante:
// - No basta ocultar botones: el backend ya valida permisos.
// - Aquí solo controlo VISIBILIDAD + UX.

import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "../api/axios";
import { resolveAvatarUrl } from "../utils/url";

export default function ChatSettingsModal({
                                              open = false,
                                              onClose,
                                              miembros = [],
                                              esAdmin = false,           // backward-compatible
                                              onAbandonarSala,
                                              salaId = null,             // necesario para ejecutar acciones
                                              onRefresh,                 // refrescar miembros tras acciones
                                              currentUserId = null,      // para saber quién soy sin depender de `m.soy`
                                          }) {
    // Hooks
    const [closing, setClosing] = useState(false);
    const [busyId, setBusyId] = useState(null); // idusuario que está ejecutando acción
    const [errorMsg, setErrorMsg] = useState(null);

    // Detecta mi miembro (por `soy` o por id)
    const myMember = useMemo(() => {
        if (currentUserId != null) {
            return miembros.find((m) => Number(m.idusuario) === Number(currentUserId)) || null;
        }
        return miembros.find((m) => !!m.soy) || null;
    }, [miembros, currentUserId]);

    const soyPropietario = !!myMember?.es_propietario;
    const soyAdmin = !!myMember?.es_admin || !!esAdmin;

    // Permisos de UI
    const canAdminActions = soyPropietario || soyAdmin; // mutear/bloquear
    const canOwnerActions = soyPropietario;             // promover/quitar admin/expulsar

    // Función de cierre
    const handleClose = useCallback(() => {
        if (closing) return;
        setClosing(true);
        setTimeout(() => {
            setClosing(false);
            onClose?.();
        }, 120);
    }, [closing, onClose]);

    // Cerrar con ESC
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") handleClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, handleClose]);

    const doRefresh = useCallback(async () => {
        try {
            await onRefresh?.();
        } catch (e) {
        }
    }, [onRefresh]);

    // Ejecuta acción backend
    const accionMiembro = useCallback(
        async (idusuario, accion) => {
            if (!salaId) return;
            setErrorMsg(null);
            setBusyId(idusuario);

            try {
                await api.post(`/salas/${salaId}/miembro/${idusuario}/accion`, { accion });
                await doRefresh();
            } catch (e) {
                console.error("Error acción miembro:", e);
                // mensaje de UX simple
                setErrorMsg("No se pudo ejecutar la acción (permisos o error de red).");
            } finally {
                setBusyId(null);
            }
        },
        [salaId, doRefresh]
    );

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/40" onClick={handleClose} aria-hidden="true" />

            {/* Modal */}
            <div
                className={`
          relative w-[92vw] max-w-xl rounded-2xl
          bg-white dark:bg-gray-900
          border border-gray-200 dark:border-gray-700
          shadow-xl p-5
          transition-transform duration-150
          ${closing ? "scale-95 opacity-0" : "scale-100 opacity-100"}
        `}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Información del chat
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                        aria-label="Cerrar"
                    >
                        ✕
                    </button>
                </div>

                <div className="space-y-4">
                    {/* Rol del usuario */}
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 text-sm">
                        <span className="font-medium">Tu rol:</span>{" "}
                        {soyPropietario ? "Propietario" : soyAdmin ? "Administrador" : "Miembro"}
                    </div>

                    {errorMsg && (
                        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
                            {errorMsg}
                        </div>
                    )}

                    {/* Miembros */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 font-medium text-sm">
                            Miembros
                        </div>

                        <ul className="max-h-[45vh] overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
                            {miembros.map((m) => {
                                const soy =
                                    currentUserId != null
                                        ? Number(m.idusuario) === Number(currentUserId)
                                        : !!m.soy;

                                const isOwner = !!m.es_propietario;
                                const isAdmin = !!m.es_admin;

                                const disabled = busyId === m.idusuario;

                                return (
                                    <li
                                        key={m.idusuario ?? m.id ?? m.email}
                                        className="p-3 flex items-center justify-between gap-3"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <Avatar nombre={m.nombre} foto={m.foto} />
                                            <div className="min-w-0">
                                                <div className="font-medium text-gray-900 dark:text-white truncate">
                                                    {m.nombre || m.email || "Usuario"}
                                                </div>
                                                <div className="text-xs opacity-70 truncate">
                                                    {m.email}
                                                    {isOwner && " • propietario"}
                                                    {!isOwner && isAdmin && " • admin"}
                                                    {m.muteado && " • muteado"}
                                                    {m.bloqueado && " • bloqueado"}
                                                    {soy && " • tú"}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Acciones a la derecha */}
                                        <div className="flex items-center gap-2 flex-wrap justify-end">
                                            {/* Acción única visible para mí */}
                                            {soy && (
                                                <button
                                                    onClick={onAbandonarSala}
                                                    className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
                                                >
                                                    Abandonar
                                                </button>
                                            )}

                                            {/* Acciones de administración*/}
                                            {!soy && canAdminActions && salaId && (
                                                <>
                                                    {/* Mutear / Desmutear */}
                                                    <button
                                                        disabled={disabled}
                                                        onClick={() =>
                                                            accionMiembro(m.idusuario, m.muteado ? "desmutear" : "mutear")
                                                        }
                                                        className={`
                              px-3 py-1.5 rounded-lg text-sm border
                              ${m.muteado ? "bg-gray-100 dark:bg-gray-800" : "bg-white dark:bg-gray-900"}
                              hover:bg-gray-50 dark:hover:bg-gray-800
                              disabled:opacity-50
                            `}
                                                        title={m.muteado ? "Quitar mute" : "Mutear"}
                                                    >
                                                        {m.muteado ? "Desmutear" : "Mutear"}
                                                    </button>

                                                    {/* Bloquear / Desbloquear */}
                                                    <button
                                                        disabled={disabled}
                                                        onClick={() =>
                                                            accionMiembro(m.idusuario, m.bloqueado ? "desbloquear" : "bloquear")
                                                        }
                                                        className={`
                              px-3 py-1.5 rounded-lg text-sm border
                              ${m.bloqueado ? "bg-gray-100 dark:bg-gray-800" : "bg-white dark:bg-gray-900"}
                              hover:bg-gray-50 dark:hover:bg-gray-800
                              disabled:opacity-50
                            `}
                                                        title={m.bloqueado ? "Quitar bloqueo" : "Bloquear"}
                                                    >
                                                        {m.bloqueado ? "Desbloq." : "Bloquear"}
                                                    </button>
                                                </>
                                            )}

                                            {/* Acciones admin-only */}
                                            {!soy && canOwnerActions && salaId && (
                                                <>
                                                    {/* Promover / Quitar admin (pero nunca al propietario) */}
                                                    {!isOwner && (
                                                        <button
                                                            disabled={disabled}
                                                            onClick={() =>
                                                                accionMiembro(
                                                                    m.idusuario,
                                                                    isAdmin ? "quitar_admin" : "promover_admin"
                                                                )
                                                            }
                                                            className={`
                                px-3 py-1.5 rounded-lg text-sm border
                                bg-white dark:bg-gray-900
                                hover:bg-gray-50 dark:hover:bg-gray-800
                                disabled:opacity-50
                              `}
                                                            title={isAdmin ? "Quitar admin" : "Hacer admin"}
                                                        >
                                                            {isAdmin ? "Quitar admin" : "Hacer admin"}
                                                        </button>
                                                    )}

                                                    {/* Expulsar (admin-only) */}
                                                    {!isOwner && (
                                                        <button
                                                            disabled={disabled}
                                                            onClick={() => accionMiembro(m.idusuario, "expulsar")}
                                                            className="
                                px-3 py-1.5 rounded-lg text-sm
                                bg-red-600 text-white hover:bg-red-700
                                disabled:opacity-50
                              "
                                                            title="Expulsar de la sala"
                                                        >
                                                            Expulsar
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// --------------
// Avatar simple
// --------------
function Avatar({ nombre, foto }) {
    const letter = (nombre || "").trim().charAt(0).toUpperCase() || "U";
    const src = resolveAvatarUrl(foto);

    return src ? (
        <img
            src={src}
            alt={nombre || "usuario"}
            className="w-9 h-9 rounded-full object-cover border"
        />
    ) : (
        <div className="w-9 h-9 rounded-full bg-blue-600 text-white grid place-items-center text-sm font-semibold">
            {letter}
        </div>
    );
}
